import { useRef, useEffect, useState, useCallback } from "react";

export const useAudioPlayback = ({
  audioState,
  dispatchAudio,
  audioUrlList,
  setAudioUrlList,
  setAudioItemList,
  chatMode,
  selectedSpeakerId,
}) => {
  const audioComponentRef = useRef(null);
  const audioUrlListRef = useRef([]);
  const playbackAudioContextRef = useRef(null);
  const playbackAnalyserRef = useRef(null);
  const playbackDataArrayRef = useRef(null);
  const playbackAnimationRef = useRef(null);
  const playbackAnimationLastUpdateRef = useRef(0);

  const recordingAudioContextRef = useRef(null);
  const recordingAnalyserRef = useRef(null);
  const recordingDataArrayRef = useRef(null);

  const [recordingCanProceed, setRecordingCanProceed] = useState(false);

  // AudioContext init effect — creates BOTH playback and recording contexts
  useEffect(() => {
    if (!audioComponentRef.current) return;

    let playbackAudioContext, playbackAnalyser, sourceNode;
    let recordingAudioContext, recordingAnalyser;

    try {
      playbackAudioContext = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      playbackAnalyser = playbackAudioContext.createAnalyser();
      playbackAnalyser.fftSize = 256;
      sourceNode = playbackAudioContext.createMediaElementSource(
        audioComponentRef.current,
      );

      sourceNode.connect(playbackAnalyser);
      playbackAnalyser.connect(playbackAudioContext.destination);

      playbackAudioContextRef.current = playbackAudioContext;
      playbackAnalyserRef.current = playbackAnalyser;
      playbackDataArrayRef.current = new Uint8Array(
        playbackAnalyser.frequencyBinCount,
      );
    } catch (e) {
      console.warn("Playback AudioContext setup failed:", e.message);
      if (playbackAudioContext) playbackAudioContext.close();
    }

    try {
      recordingAudioContext = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      recordingAnalyser = recordingAudioContext.createAnalyser();
      recordingAnalyser.fftSize = 2048;
      recordingAnalyser.smoothingTimeConstant = 0.5;

      recordingAudioContextRef.current = recordingAudioContext;
      recordingAnalyserRef.current = recordingAnalyser;
      recordingDataArrayRef.current = new Float32Array(recordingAnalyser.fftSize);
    } catch (e) {
      console.warn("Recording AudioContext setup failed:", e.message);
      if (recordingAudioContext) recordingAudioContext.close();
    }

    setRecordingCanProceed(true);

    return () => {
      if (sourceNode) sourceNode.disconnect();
      if (playbackAnalyser) playbackAnalyser.disconnect();
      if (playbackAudioContext) playbackAudioContext.close();
      if (recordingAnalyser) recordingAnalyser.disconnect();
      if (recordingAudioContext) recordingAudioContext.close();
    };
  }, []);

  // Resume AudioContext on any user interaction (browsers may suspend it)
  useEffect(() => {
    if (chatMode !== "AUDIO") return;

    const resumeContext = () => {
      if (playbackAudioContextRef.current?.state === "suspended") {
        playbackAudioContextRef.current.resume();
      }
    };

    document.addEventListener("click", resumeContext);
    document.addEventListener("touchstart", resumeContext);
    document.addEventListener("keydown", resumeContext);

    return () => {
      document.removeEventListener("click", resumeContext);
      document.removeEventListener("touchstart", resumeContext);
      document.removeEventListener("keydown", resumeContext);
    };
  }, [chatMode]);

  // Speaker device selection effect
  useEffect(() => {
    if (
      audioComponentRef.current &&
      selectedSpeakerId &&
      "setSinkId" in HTMLMediaElement.prototype
    ) {
      audioComponentRef.current.setSinkId(selectedSpeakerId).catch((error) => {
        console.error("Error setting audio output device:", error);
      });
    }
  }, [selectedSpeakerId]);

  // Playback rate sync effect
  useEffect(() => {
    if (audioComponentRef.current) {
      audioComponentRef.current.playbackRate = audioState.playbackRate;
    }
  }, [audioState.playbackRate]);

  const stopAndCleanupAudio = useCallback(() => {
    if (audioComponentRef.current) {
      audioComponentRef.current.pause();
      audioComponentRef.current.currentTime = 0;
      audioComponentRef.current.src = "";
    }
    if (playbackAnimationRef.current) {
      cancelAnimationFrame(playbackAnimationRef.current);
      playbackAnimationRef.current = null;
    }
    setAudioUrlList([]);
    setAudioItemList([]);
  }, []);

  const handlePausePlayback = useCallback(() => {
    if (
      audioComponentRef.current &&
      audioState.isPlaying &&
      !audioState.isPaused
    ) {
      audioComponentRef.current.pause();
      dispatchAudio({ type: "PAUSE_PLAYBACK" });
    }
  }, [audioState.isPlaying, audioState.isPaused]);

  const handleResumePlayback = useCallback(async () => {
    if (audioComponentRef.current && audioState.isPaused) {
      try {
        if (playbackAudioContextRef.current?.state === "suspended") {
          await playbackAudioContextRef.current.resume();
        }
        await audioComponentRef.current.play();
        dispatchAudio({ type: "RESUME_PLAYBACK" });
      } catch (error) {
        console.error("Error resuming playback:", error);
      }
    }
  }, [audioState.isPaused]);

  const stopAudioAnimation = useCallback(() => {
    if (playbackAnimationRef.current) {
      cancelAnimationFrame(playbackAnimationRef.current);
      playbackAnimationRef.current = null;
    }
    dispatchAudio({ type: "SET_MOUTH_STATE", payload: "CLOSED" });
  }, []);

  const retrieveAudioMouthState = useCallback(
    (rms) => {
      if (chatMode === "TEXT") return "CLOSED";

      const now = Date.now();
      if (now - playbackAnimationLastUpdateRef.current < 75) return null;
      playbackAnimationLastUpdateRef.current = now;

      if (rms > 0.05) return "WIDE_OPEN";
      if (rms > 0.025) return Math.random() < 0.5 ? "HALF_OPEN_2" : "HALF_OPEN";
      return "CLOSED";
    },
    [chatMode],
  );

  const animateAudio = useCallback(() => {
    if (chatMode === "TEXT") {
      stopAudioAnimation();
      return;
    }

    const analyser = playbackAnalyserRef.current;
    const dataArray = playbackDataArrayRef.current;
    if (!analyser || !dataArray) return;

    analyser.getByteTimeDomainData(dataArray);
    const rms = Math.sqrt(
      Array.from(dataArray as Uint8Array).reduce(
        (sum: number, val: number) => sum + ((val - 128) / 128) ** 2,
        0,
      ) / (dataArray as Uint8Array).length,
    );

    const newState = retrieveAudioMouthState(rms);
    if (newState) dispatchAudio({ type: "SET_MOUTH_STATE", payload: newState });

    playbackAnimationRef.current = requestAnimationFrame(animateAudio);
  }, [retrieveAudioMouthState, stopAudioAnimation, chatMode]);

  const startAudioAnimation = useCallback(() => {
    if (
      !playbackAnimationRef.current &&
      playbackAudioContextRef.current &&
      chatMode === "AUDIO"
    ) {
      if (playbackAudioContextRef.current.state === "closed") return;
      playbackAudioContextRef.current.resume().then(() => {
        playbackAnimationRef.current = requestAnimationFrame(animateAudio);
      });
    }
  }, [chatMode, animateAudio]);

  const playNextAudio = useCallback(() => {
    if (chatMode !== "AUDIO") {
      stopAndCleanupAudio();
      return;
    }

    if (
      audioState.playIndex < audioUrlList.length &&
      !audioState.isPlaying &&
      !audioState.isPaused &&
      audioState.activePlayIndex < audioState.playIndex
    ) {
      dispatchAudio({
        type: "SET_READY",
        payload: audioUrlList[audioState.playIndex],
      });
    }
  }, [
    chatMode,
    audioState.playIndex,
    audioState.isPlaying,
    audioState.isPaused,
    audioState.activePlayIndex,
    audioUrlList,
    stopAndCleanupAudio,
  ]);

  // Mouth animation start/stop effect
  useEffect(() => {
    if (chatMode === "TEXT") {
      stopAudioAnimation();
      return;
    }
    audioState.isPlaying && !audioState.isPaused
      ? startAudioAnimation()
      : stopAudioAnimation();
  }, [
    audioState.isPlaying,
    audioState.isPaused,
    chatMode,
    startAudioAnimation,
    stopAudioAnimation,
  ]);

  // audioUrlListRef sync + mode reset effect
  useEffect(() => {
    if (chatMode === "TEXT") {
      dispatchAudio({ type: "RESET" });
    } else {
      if (audioUrlList.length === 0) {
        if (audioUrlListRef.current.length === 0) return;
        audioUrlListRef.current = [];
        dispatchAudio({ type: "RESET_INDICES" });
      } else {
        audioUrlListRef.current = audioUrlList;
      }
    }
  }, [chatMode, audioUrlList]);

  // All-played wait-for-user-voice trigger effect
  useEffect(() => {
    if (
      chatMode !== "AUDIO" ||
      !audioState.isConversionComplete ||
      audioUrlList.length === 0 ||
      audioState.playIndex < audioUrlList.length ||
      !audioState.isEnded
    )
      return;
    dispatchAudio({ type: "SET_WAITING_FOR_USER_VOICE" });
  }, [
    chatMode,
    audioState.isConversionComplete,
    audioState.playIndex,
    audioUrlList,
    audioState.isEnded,
  ]);

  // TEXT mode end-playback cleanup effect
  useEffect(() => {
    if (
      chatMode !== "TEXT" ||
      !audioState.isEnded ||
      audioState.playIndex < audioUrlList.length - 1 ||
      audioState.isWaitingForUserVoice
    ) {
      return;
    }

    dispatchAudio({ type: "END_PLAYBACK", payload: { reset: true } });
  }, [chatMode, audioState]);

  // End-of-track chaining effect (isEnded)
  useEffect(() => {
    if (
      chatMode !== "AUDIO" ||
      !audioState.isEnded ||
      audioState.playIndex < audioUrlList.length ||
      audioState.isWaitingForUserVoice ||
      audioState.isPaused
    ) {
      return;
    }
    playNextAudio();
  }, [
    audioState.isEnded,
    audioState.isPaused,
    chatMode,
    playNextAudio,
    audioState.playIndex,
    audioUrlList,
    audioState.isWaitingForUserVoice,
  ]);

  // End-of-track chaining effect (new URLs available)
  useEffect(() => {
    if (
      chatMode !== "AUDIO" ||
      audioUrlList.length === 0 ||
      audioState.playIndex >= audioUrlList.length ||
      audioState.isPlaying ||
      audioState.isPaused ||
      audioState.isReady ||
      audioState.isWaitingForUserVoice
    ) {
      return;
    }
    playNextAudio();
  }, [
    chatMode,
    audioUrlList,
    audioState.playIndex,
    audioState.isPlaying,
    audioState.isPaused,
    audioState.isReady,
    audioState.isWaitingForUserVoice,
    playNextAudio,
  ]);

  // Audio ready -> play effect (sets src, calls play())
  useEffect(() => {
    if (
      chatMode !== "AUDIO" ||
      !audioComponentRef.current ||
      !audioState.currentUrl ||
      !audioState.isReady ||
      audioState.isPlaying ||
      audioState.isPaused
    ) {
      return;
    }

    const playAudio = async () => {
      try {
        if (playbackAudioContextRef.current?.state === "suspended") {
          await playbackAudioContextRef.current.resume();
        }
        audioComponentRef.current.src = audioState.currentUrl;
        audioComponentRef.current.playbackRate = audioState.playbackRate;
        await audioComponentRef.current.play();
        dispatchAudio({ type: "START_PLAYBACK" });
      } catch (error) {
        console.error("Audio playback failed:", error);
        dispatchAudio({ type: "END_PLAYBACK" });
      }
    };
    playAudio();
  }, [
    chatMode,
    audioState.currentUrl,
    audioState.isReady,
    audioState.isPlaying,
    audioState.isPaused,
    audioState.playbackRate,
  ]);

  return {
    audioComponentRef,
    playbackAudioContextRef,
    stopAndCleanupAudio,
    handlePausePlayback,
    handleResumePlayback,
    recordingAudioContextRef,
    recordingAnalyserRef,
    recordingDataArrayRef,
    recordingCanProceed,
    setRecordingCanProceed,
  };
};
